import { ArrowUpRight, Check } from "lucide-react";
import { COURSES, bookingHref } from "../../content";
import { Eyebrow, Ph, Reveal } from "./Primitives";

const Meta = ({ label, value, ph }) => (
  <div className="flex flex-col gap-1">
    <dt className="text-[11px] font-bold uppercase tracking-[0.2em] text-slate-500">{label}</dt>
    <dd className="text-sm font-semibold text-navy">{value ?? <Ph>{ph}</Ph>}</dd>
  </div>
);

const GoalList = ({ items }) => (
  <ul className="mt-2 space-y-1.5">
    {items.map((g) => (
      <li key={g} className="flex gap-2 text-sm text-navy">
        <Check className="mt-0.5 h-4 w-4 shrink-0 text-sky-600" /> {g}
      </li>
    ))}
  </ul>
);

const CourseCard = ({ c, i }) => (
  <Reveal delay={(i % 3) * 0.08} className="h-full">
    <article data-testid={`course-card-${c.slug}`} className="course-card group flex h-full flex-col border border-navy/10 bg-white transition-[border-color,box-shadow] duration-500 hover:border-navy/30 hover:shadow-[0_30px_60px_-35px_rgba(10,27,51,0.45)]">
      <div className="relative aspect-[4/3] overflow-hidden">
        <img src={c.image} alt={c.alt} loading="lazy" className="course-img h-full w-full object-cover" />
        <span className="absolute left-4 top-4 bg-white/95 px-3 py-1 text-[11px] font-bold uppercase tracking-[0.2em] text-navy">Stufe {c.level}</span>
      </div>
      <div className="flex flex-1 flex-col p-6 sm:p-7">
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-sky-700">{c.kicker}</p>
        <h3 className="mt-2 text-2xl font-extrabold tracking-tight text-navy sm:text-3xl">{c.name}</h3>
        <p className="mt-3 text-sm leading-relaxed text-slate-600 sm:text-base">{c.text}</p>
        <div className="mb-6 mt-5">
          {c.goalGroups ? (
            c.goalGroups.map((grp) => (
              <div key={grp.title} className="mt-4 first:mt-0">
                <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-slate-500">{grp.title} – Lernziele</p>
                <GoalList items={grp.items} />
              </div>
            ))
          ) : (
            <>
              <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-slate-500">Lernziele</p>
              {c.goals ? <GoalList items={c.goals} /> : <div className="mt-2"><Ph>Lernziele ergänzen</Ph></div>}
            </>
          )}
        </div>
        <dl className="mt-auto grid grid-cols-2 gap-4 border-t border-navy/10 pt-5">
          <Meta label="Alter / Niveau" value={c.age} ph="Alter ergänzen" />
          <Meta label="Voraussetzung" value={c.prereq} ph="Voraussetzung ergänzen" />
        </dl>
        <a
          href={bookingHref(c)}
          target="_blank"
          rel="noopener noreferrer"
          data-testid={`course-book-${c.slug}`}
          className="flex items-center justify-between pt-7 text-sm font-bold text-navy"
        >
          <span className="relative after:absolute after:-bottom-1 after:left-0 after:h-px after:w-full after:origin-left after:scale-x-0 after:bg-navy after:transition-transform after:duration-500 group-hover:after:scale-x-100">Jetzt buchen</span>
          <span className="flex h-10 w-10 items-center justify-center rounded-full border border-navy/20 transition-[background-color,color,transform] duration-500 group-hover:rotate-45 group-hover:bg-navy group-hover:text-white">
            <ArrowUpRight className="h-4 w-4" />
          </span>
        </a>
      </div>
    </article>
  </Reveal>
);

export const Courses = () => (
  <section id="kurse" data-testid="courses-section" className="bg-white py-24 sm:py-32">
    <div className="mx-auto max-w-[1400px] px-5 sm:px-8 lg:px-12">
      <div className="grid gap-8 lg:grid-cols-12 lg:items-end">
        <Reveal className="lg:col-span-7">
          <Eyebrow>Schwimmkurse für Kinder in Dieburg</Eyebrow>
          <h2 className="mt-6 text-4xl font-extrabold leading-[1.05] tracking-[-0.02em] text-navy sm:text-5xl lg:text-6xl">
            Drei Kurse. <span className="font-light italic text-sky-700">Ein Weg ins Wasser.</span>
          </h2>
        </Reveal>
        <Reveal delay={0.1} className="lg:col-span-5">
          <p className="text-base leading-relaxed text-slate-600 md:text-lg">
            Vom ersten Kontakt mit dem Wasser bis zum Schwimmabzeichen in Bronze – jeder Kurs baut auf dem vorherigen auf: Froschkurs für Anfänger, Seepferdchenkurs und Seeräuber- & Bronze-Schwimmkurs im Sportbad Dieburg, für Kinder aus Dieburg, Darmstadt und dem Landkreis Darmstadt-Dieburg. Die Lernziele orientieren sich am Deutschen Schwimmabzeichen. Aktuelle Termine, Preise und freie Plätze finden Sie in der Kursbuchung.
          </p>
        </Reveal>
      </div>
      <div className="mt-16 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
        {COURSES.map((c, i) => <CourseCard key={c.slug} c={c} i={i} />)}
      </div>
    </div>
  </section>
);
